import $ from 'jquery';

// 選んだ動画ファイルから静止画を切り出し、カスタムサムネイルの候補として並べる。
// 選択された候補は custom_thumbnail の file input に入るので、そのままフォーム送信で保存される。
// @see https://github.com/exestinc/video_db/issues/2483

// 切り出す位置 (動画長に対する割合)。冒頭は暗転していることが多いので 0% は使わない。
const CAPTURE_POSITIONS = [0.1, 0.3, 0.5, 0.7, 0.9];

// CustomThumbnailUploader の extension_allowlist に jpg が含まれている
const THUMBNAIL_TYPE = 'image/jpeg';
const THUMBNAIL_EXTENSION = 'jpg';
const THUMBNAIL_QUALITY = 0.92;

// 4K の動画をそのまま書き出すと数MBになるため、長辺をこのサイズに収める
const MAX_WIDTH = 1280;

// 壊れた動画などで seek が返ってこないとローディングが残り続けるため、1枚あたりで打ち切る
const SEEK_TIMEOUT_MS = 5000;

// input.files を差し替えられないブラウザではこの機能を出さない (通常のファイル選択は使える)
const canAssignFiles = () => {
  if (typeof DataTransfer === 'undefined') return false;

  try {
    return 'files' in new DataTransfer();
  } catch {
    return false;
  }
};

const loadVideo = (file) => new Promise((resolve, reject) => {
  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.src = URL.createObjectURL(file);

  video.addEventListener('loadeddata', () => resolve(video), { once: true });
  video.addEventListener('error', () => reject(new Error('動画を読み込めませんでした')), { once: true });
});

const seekTo = (video, time) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('シークがタイムアウトしました')), SEEK_TIMEOUT_MS);

  video.addEventListener('seeked', () => {
    clearTimeout(timer);
    resolve();
  }, { once: true });

  video.currentTime = time;
});

const captureFrame = (video) => new Promise((resolve, reject) => {
  const scale = Math.min(1, MAX_WIDTH / video.videoWidth);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(video.videoWidth * scale);
  canvas.height = Math.round(video.videoHeight * scale);
  canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);

  // blob: の動画から描いた canvas は汚染されないが、念のため失敗を握れるようにしておく
  canvas.toBlob((blob) => {
    if (blob) {
      resolve(blob);
    } else {
      reject(new Error('画像を書き出せませんでした'));
    }
  }, THUMBNAIL_TYPE, THUMBNAIL_QUALITY);
});

export default function() {
  const $fileInput = $('.js-video-file-input');
  const $candidates = $('.js-thumbnail-candidates');
  const $status = $('.js-thumbnail-candidates-status');
  const thumbnailInput = document.querySelector('.js-custom-thumbnail-input');

  if ($fileInput.length === 0 || $candidates.length === 0 || thumbnailInput === null) return;
  if (!canAssignFiles()) return;

  const selectCandidate = (blob, $image) => {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(new File([blob], `thumbnail.${THUMBNAIL_EXTENSION}`, { type: THUMBNAIL_TYPE }));
    thumbnailInput.files = dataTransfer.files;
    // ファイル名の表示など、他のスクリプトが change を見ている場合に備えて発火させる
    thumbnailInput.dispatchEvent(new Event('change', { bubbles: true }));

    $candidates.find('img').removeClass('js-thumbnail-candidate-selected');
    $image.addClass('js-thumbnail-candidate-selected');
    $status.text('この画像をカスタムサムネイルとして登録します。');
  };

  const appendCandidate = (blob) => {
    const $image = $('<img>', {
      src: URL.createObjectURL(blob),
      alt: 'サムネイル候補',
      width: 160,
      class: 'mr-2 mb-2',
      css: { cursor: 'pointer' }
    });

    $image.on('click', () => selectCandidate(blob, $image));
    $candidates.append($image);
  };

  $fileInput.on('change', async (event) => {
    $candidates.empty();
    $status.text('');

    const file = event.currentTarget.files[0];
    if (!file) return;

    $status.text('サムネイルの候補を作成しています...');

    let video;
    try {
      video = await loadVideo(file);
    } catch {
      // 対応していない形式などで読み込めない場合。手動アップロードは使えるので黙って諦める
      $status.text('');
      return;
    }

    // duration が取れない動画 (ストリーミング等) では位置を決められない
    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      $status.text('');
      URL.revokeObjectURL(video.src);
      return;
    }

    // 同時にシークすると最後の指定しか効かないため、1枚ずつ順番に処理する
    for (const position of CAPTURE_POSITIONS) {
      try {
        await seekTo(video, video.duration * position);
        appendCandidate(await captureFrame(video));
      } catch {
        // 1枚失敗しても残りは試す
      }
    }

    URL.revokeObjectURL(video.src);
    $status.text($candidates.children().length > 0
      ? '使いたい画像をクリックすると、カスタムサムネイルとして登録されます。'
      : '');
  });
}
